import { IBidIdentifier } from 'src/models/bids/Bids/types';
import { DndType } from 'src/types/dnd';
import { BidItemType } from '@tendium/prom-types/tender';
import { PageView } from 'src/models/users/Preferences/types';
import { OverviewQp, ActivePreviewTab } from '../types';
import { AwardStatus, BuyerType, ContractEndSearchType, JunctionLogic, SearchOnlyStatus } from '@tendium/prom-types';
import { isNumbers, isObject, isString } from 'src/helpers';
import { TendersTableItem } from '../TendersViews';
import { IApiTenderHighlight } from '../Preview/types';
import { DocumentNode, TypedDocumentNode } from '@apollo/client';
import { IApiTender, IApiTenderBox, ITenderGeneral, ITenderTimeline } from '../Tender/types';
import { ContractValueRange, MpNewKeyword } from 'src/models/matchingProfiles/types';

export const DEFAULT_TENDERS_VIEW = PageView.table;
export const DEFAULT_TENDERS_TAB = ActivePreviewTab.Info;
export const DEFAULT_INBOX_TAB = ActivePreviewTab.Email;
export const DEFAULT_OVERVIEW_QP = {
  [OverviewQp.WorkspaceId]: undefined,
  [OverviewQp.MatchingProfileId]: undefined,
  [OverviewQp.CommentId]: undefined,
  [OverviewQp.FromProcurement]: undefined
};

export interface ITendersDndItem {
  type: DndType.TendersItem;
  dndItem: TendersTableItem;
  originIds?: string[];
  originType?: BidItemType;
  bidIds?: IBidIdentifier[];
}

export type TendersSortVars = { key: TendersSortName; sort: TendersSortOrder };
type ExpiringContractSearchInput = {
  start: number;
  end: number;
  searchMode: ContractEndSearchType;
};
type DateFilter = {
  start: number;
  end: number;
};
export type SupplierOrg = {
  orgId: string;
  status: AwardStatus;
};
export type SupplierString = {
  orgName: string;
  status: AwardStatus;
};
export type ApiTendersSearchInput = {
  query?: Omit<ApiTendersVars, 'advanced'>;
  advanced?: ApiTendersAdvancedVars;
};
export type ApiTendersVars = {
  amount?: number;
  isStarred?: boolean;
  isUnhandled?: boolean;
  matchingProfileId?: string;
  offset?: number;
  sortOnField?: TendersSortVars;
  advanced?: ApiTendersAdvancedVars;
};
export type ApiTendersAdvancedVars = {
  assignedTo?: string[];
  awarded?: boolean;
  buyerOrgId?: string;
  buyerOrgIds?: string[];
  buyerSearchStrings?: string[];
  contractEnd?: ExpiringContractSearchInput;
  contractValueRange?: ContractValueRange;
  cpvCodes?: string[];
  cpvCodesExact?: string[];
  documentSearch?: boolean;
  highlight?: boolean;
  keywordArgs?: MpNewKeyword[];
  name?: string;
  nutsCodes?: string[];
  nutsCodesExact?: string[];
  onlyFilledData?: boolean;
  onlyStatus?: SearchOnlyStatus;
  procuringAgency?: string;
  procuringAgencyTypes?: BuyerType[];
  publicationDate?: DateFilter;
  search?: string[];
  submissionDeadline?: DateFilter;
  supplierOrgIds?: SupplierOrg[];
  supplierSearchLogic?: JunctionLogic;
  supplierSearchStrings?: SupplierString[];
  contractValueIncludeMissingValue?: boolean;
  profileId?: string;
};

export type StarredTendersVars = Pick<
  TendersVars,
  'amount' | 'offset' | 'sort' | 'search' | 'onlyStatus' | 'awarded' | 'isStarred'
>;
export type AllTendersVars = Pick<
  TendersVars,
  | 'amount'
  | 'offset'
  | 'sort'
  | 'search'
  | 'onlyStatus'
  | 'assignedTo'
  | 'cpvCodes'
  | 'nutsCodes'
  | 'deadline'
  | 'published'
  | 'buyerOrgIds'
  | 'buyerSearchStrings'
  | 'awarded'
>;
export type MpTendersVars = Pick<
  TendersVars,
  'amount' | 'offset' | 'awarded' | 'sort' | 'search' | 'onlyStatus' | 'matchingProfileId' | 'isUnhandled'
>;
export type BuyersTendersVars = Pick<TendersVars, 'amount' | 'offset' | 'sort' | 'onlyStatus' | 'buyerOrgId'>;
export type ExpContractTendersVars = Pick<
  TendersVars,
  | 'amount'
  | 'offset'
  | 'sort'
  | 'search'
  | 'contractEnd'
  | 'contractEndSearchMode'
  | 'contractValueRange'
  | 'supplierOrgIds'
  | 'supplierSearchStrings'
  | 'buyerOrgIds'
  | 'buyerSearchStrings'
  | 'keywordArgs'
  | 'cpvCodes'
  | 'cpvCodesExact'
  | 'nutsCodes'
  | 'nutsCodesExact'
  | 'buyerOrgId'
  | 'profileId'
  | 'supplierSearchLogic'
  | 'contractValueIncludeMissingValue'
  | 'procuringAgencyTypes'
>;

export type TendersItemCustomData = {
  isCommented: boolean;
  highlights?: IApiTenderHighlight[];
  nextId?: string;
  bid?: IBidIdentifier;
  matchedCpvs?: string[];
};

export interface ApiTendersTimeline<T> extends Pick<ITenderTimeline<T>, 'availableDate' | 'deadline'> {}
export interface ApiTendersGeneral<T>
  extends Pick<ITenderGeneral<T>, 'name' | 'buyerBoxes' | 'contractDurationBoxes' | 'contractValueBoxes'> {}
export type ApiTendersItem = Omit<
  IApiTender,
  'general' | 'timeline' | 'custom' | 'boxes' | 'fileCategories' | 'comments'
> & {
  timeline: ApiTendersTimeline<IApiTenderBox>;
  general: ApiTendersGeneral<IApiTenderBox>;
  rejected: boolean | null;
};
export type TendersItem<TData extends ApiTendersItem> = TData & TendersItemCustomData;

export type ApiTendersResponse<TData> = {
  count: number;
  procurementsWithScore: {
    procurement: TData;
    highlights: IApiTenderHighlight[];
    matchedCpvs: string[];
  }[];
};
export type ApiTendersData = {
  getTenders: ApiTendersResponse<ApiTendersItem>;
};
export type UseApiTendersCtxOptions<TVars> = {
  query?: DocumentNode | TypedDocumentNode<ApiTendersData, ApiTendersVars>;
  initVars: TVars;
};

export enum TendersSortName {
  published = 'Available',
  title = 'Name',
  buyer = 'Buyer',
  deadline = 'Deadline',
  expiration = 'Expiration'
}
export enum TendersSortOrder {
  ASCENDING = 'ASCENDING',
  DESCENDING = 'DESCENDING'
}
export const SORTABLE_TENDERS_COLUMNS = [
  TendersSortName.published,
  TendersSortName.title,
  TendersSortName.buyer,
  TendersSortName.deadline
];

export type DateRange = [number, number];
export type TendersVars = Omit<ApiTendersVars, 'sortOnField' | 'advanced'> &
  Omit<
    ApiTendersAdvancedVars,
    'submissionDeadline' | 'contractEnd' | 'publicationDate' | 'procuringAgency' | 'search'
  > & {
    sort?: TendersSortVars;
    published?: DateRange;
    deadline?: DateRange;
    contractEnd?: DateRange;
    contractEndSearchMode?: ContractEndSearchType;
    search?: string;
    agency?: string;
  };

export type MappedTendersTypes = {
  All: AllTendersVars;
  Starred: StarredTendersVars;
  Mp: MpTendersVars;
  ExpContract: ExpContractTendersVars;
  BuyerExpired: BuyersTendersVars;
  BuyerRecent: BuyersTendersVars;
};
export type TendersTypes = keyof MappedTendersTypes;
type MapTypesKeys<T> = {
  [P in keyof T]: Array<keyof T[P]>;
};
export type MapTypeKeys<T> = Array<keyof T>;
export type TendersTypesVars = MapTypesKeys<MappedTendersTypes>;
export enum TendersSearchParams {
  sort = 'sort',
  isUnhandled = 'isUnhandled',
  search = 'search',
  name = 'name',
  agency = 'agency',
  deadline = 'deadline',
  published = 'published',
  assignedTo = 'assignedTo',
  awarded = 'awarded',
  cpvCodes = 'cpvCodes',
  nutsCodes = 'nutsCodes',
  onlyStatus = 'onlyStatus',
  matchingProfileId = 'matchingProfileId',
  buyerOrgIds = 'buyerOrgIds',
  buyerSearchStrings = 'buyerSearchStrings',
  profileId = 'profileId'
}

export function isDateRange(u: unknown): u is DateRange {
  return isNumbers(u) && u.length === 2;
}
export function isTendersSortOrder(u: unknown): u is TendersSortOrder {
  return isString(u) && Object.values(TendersSortOrder).some(c => c === u);
}
export function isTendersSortName(u: unknown): u is TendersSortName {
  return isString(u) && Object.values(TendersSortName).some(c => c === u);
}
export function isTendersSort(u: unknown): u is TendersSortVars {
  return isObject(u) && 'key' in u && 'sort' in u && isTendersSortName(u.key) && isTendersSortOrder(u.sort);
}
export function isTendersDndItem(u: unknown): u is ITendersDndItem {
  return isObject(u) && 'type' in u && u.type === DndType.TendersItem;
}
export function isTendersExpirationStatus(u: unknown): u is SearchOnlyStatus {
  return isString(u) && Object.values(SearchOnlyStatus).some(c => c === u);
}
